export function searchInput () {
  const ACTIVE_CLASS = 'farlo-ui-search-bar--active';
  const ROOT_SEL = '.farlo-ui-search-bar';
  const BTN_SEL = '.farlo-ui-search-bar__button';
  const FORM_SEL = '.farlo-ui-search-bar__form';
  const INPUT_SEL = '.farlo-ui-search-bar__form-input';
  const RESULTS_SEL = '.farlo-ui-search-bar__results';
  const RESULTS_HAS_ITEMS_CLASS = 'farlo-ui-search-bar__results--has-items';
  const RESULTS_OPEN_CLASS = 'farlo-ui-search-bar__results--open'; // for opacity, etc.
  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

  const ajaxUrl = (window.FarloSearch && window.FarloSearch.ajax_url) || '/wp-admin/admin-ajax.php';
  const nonce = (window.FarloSearch && window.FarloSearch.nonce) || '';

  function debounce(fn, ms) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
  }

  function ensureResultsEl(root) {
    let el = root.querySelector(RESULTS_SEL);
    if (!el) {
      el = document.createElement('div');
      el.className = 'farlo-ui-search-bar__results';
      el.setAttribute('role', 'listbox');
      el.setAttribute('aria-label', 'Search suggestions');
      el.hidden = true;
      el.style.height = '0px';
      root.appendChild(el);
    }
    return el;
  }

  // --- Smooth open/close helpers ---
  function onTransitionEndOnce(el, cb) {
    const handler = (e) => { if (e.target === el && e.propertyName === 'height') { el.removeEventListener('transitionend', handler); cb(); } };
    el.addEventListener('transitionend', handler);
  }

  function smoothExpand(el) {
    el.hidden = false;
    el.classList.add(RESULTS_OPEN_CLASS, RESULTS_HAS_ITEMS_CLASS);
    if (prefersReduced) { el.style.height = 'auto'; return; }

    // Start from current rendered height
    const start = el.getBoundingClientRect().height;
    el.style.height = start + 'px';

    // Measure target height with content
    el.style.height = 'auto';
    const target = el.scrollHeight;
    el.style.height = start + 'px'; // reset back to start

    // Animate to target
    // force reflow
    void el.offsetHeight;
    el.style.height = target + 'px';

    onTransitionEndOnce(el, () => {
      // Lock to auto so dynamic content changes don't jump
      el.style.height = 'auto';
    });
  }

  function smoothResizeToContent(el) {
    if (el.hidden || prefersReduced) return;
    // From current pixel height to new scrollHeight
    const from = el.getBoundingClientRect().height;
    el.style.height = from + 'px';
    // force reflow
    void el.offsetHeight;
    el.style.height = 'auto';
    const to = el.scrollHeight;
    el.style.height = from + 'px';
    // force reflow
    void el.offsetHeight;
    el.style.height = to + 'px';
    onTransitionEndOnce(el, () => { el.style.height = 'auto'; });
  }

  function smoothCollapse(el) {
    if (el.hidden) return;
    el.classList.remove(RESULTS_OPEN_CLASS);
    if (prefersReduced) { el.hidden = true; el.classList.remove(RESULTS_HAS_ITEMS_CLASS); el.style.height = '0px'; return; }

    const from = el.getBoundingClientRect().height || el.scrollHeight;
    el.style.height = from + 'px';
    // force reflow
    void el.offsetHeight;
    el.style.height = '0px';

    onTransitionEndOnce(el, () => {
      el.hidden = true;
      el.classList.remove(RESULTS_HAS_ITEMS_CLASS);
    });
  }

  function clearResults(resultsEl) {
    resultsEl.innerHTML = '';
    resultsEl.dataset.index = '-1';
    smoothCollapse(resultsEl);
  }

  function renderResults(resultsEl, items) {
    resultsEl.innerHTML = '';
    resultsEl.dataset.index = '-1';

    if (!items.length) {
      clearResults(resultsEl);
      return;
    }

    const ul = document.createElement('ul');
    ul.className = 'farlo-ui-search-bar__results-list';

    items.forEach((item, i) => {
      const li = document.createElement('li');
      li.className = 'farlo-ui-search-bar__results-item';
      li.setAttribute('role', 'option');
      li.setAttribute('id', `farlo-suggest-${item.id}`);
      li.setAttribute('data-index', String(i));

      const a = document.createElement('a');
      a.className = 'farlo-ui-search-bar__results-link';
      a.href = item.url;
      a.textContent = item.title;

      li.appendChild(a);
      ul.appendChild(li);
    });

    resultsEl.appendChild(ul);

    // Open if closed, else smoothly adjust height to new content
    if (resultsEl.hidden || !resultsEl.classList.contains(RESULTS_OPEN_CLASS)) {
      smoothExpand(resultsEl);
    } else {
      resultsEl.classList.add(RESULTS_HAS_ITEMS_CLASS);
      smoothResizeToContent(resultsEl);
    }
  }

  async function fetchSuggestions(term) {
    const body = new URLSearchParams();
    body.set('action', 'farlo_search_suggest');
    body.set('q', term);
    body.set('nonce', nonce);

    const res = await fetch(ajaxUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body,
      credentials: 'same-origin'
    });

    if (!res.ok) return [];
    const json = await res.json();
    return (json && json.data && Array.isArray(json.data.items)) ? json.data.items : [];
  }

  function moveHighlight(resultsEl, dir) {
    const items = resultsEl.querySelectorAll('.farlo-ui-search-bar__results-item');
    if (!items.length) return;

    let idx = parseInt(resultsEl.dataset.index || '-1', 10);
    idx = (idx + dir + items.length) % items.length;
    resultsEl.dataset.index = String(idx);

    items.forEach((li, i) => {
      if (i === idx) {
        li.classList.add('is-active');
        li.setAttribute('aria-selected', 'true');
        li.scrollIntoView({ block: 'nearest' });
      } else {
        li.classList.remove('is-active');
        li.removeAttribute('aria-selected');
      }
    });
  }

  function currentHighlighted(resultsEl) {
    const idx = parseInt(resultsEl.dataset.index || '-1', 10);
    if (idx < 0) return null;
    const items = resultsEl.querySelectorAll('.farlo-ui-search-bar__results-item');
    return items[idx] || null;
  }

  function initOne(root) {
    if (root.dataset.farloSearchInit === '1') return;
    root.dataset.farloSearchInit = '1';

    const form = root.querySelector(FORM_SEL);
    const input = root.querySelector(INPUT_SEL);
    const resultsEl = ensureResultsEl(root);
    if (!form || !input) return;

    // Button: first click opens; second click submits if value, else closes
    root.addEventListener('click', (e) => {
      const btn = e.target.closest(BTN_SEL);
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();

      if (!root.classList.contains(ACTIVE_CLASS)) {
        root.classList.add(ACTIVE_CLASS);
        document.body.classList.add('farlo-ui-search--active');
        input.focus();
      } else {
        const term = input.value.trim();
        if (!term) {
          root.classList.remove(ACTIVE_CLASS);
          document.body.classList.remove('farlo-ui-search--active');
          clearResults(resultsEl);
        } else {
          form.submit();
        }
      }
    }, { capture: true });

    // Block anchor default inside the search root (if your walker has <a>)
    // Typing → suggestions
    const onType = debounce(async () => {
      const term = input.value.trim();
      if (term.length < 2) { clearResults(resultsEl); return; }
      const items = await fetchSuggestions(term);
      renderResults(resultsEl, items);
    }, 200);

    input.addEventListener('input', onType);

    // Keyboard interactions
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { clearResults(resultsEl); return; }
      if (resultsEl.hidden) return;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); moveHighlight(resultsEl, +1); break;
        case 'ArrowUp':   e.preventDefault(); moveHighlight(resultsEl, -1); break;
        case 'Enter': {
          const active = currentHighlighted(resultsEl);
          if (active) {
            e.preventDefault();
            const a = active.querySelector('a');
            if (a) window.location.href = a.href;
          }
          break;
        }
      }
    });


    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) clearResults(resultsEl);
    }, { capture: true });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SEL).forEach(initOne);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
}
